import type { FileSetProgress, FileSetListener } from '../upload/uploadProgression';
import { OperationConclusion } from '../dto/progress';
import { exceptionAnalysis, allExceptionDetails, exceptionSummary } from '../util/exception';
import { assert } from '../util/invariant';
import type { CancelToken } from 'axios';
import Axios from 'axios';
import noop from 'lodash/noop';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import type { RollbackHandler, TaskContext, TransientErrorHandler } from './taskContext';
import { runTask } from './taskScope';

/*
  Builds and fires progress structures to listeners.
*/
export class ProgressBuilder implements TransientErrorHandler {
  readonly errors: Set<string>;
  private cancelling: boolean = false;
  constructor(public readonly progress: FileSetProgress, public readonly listener: FileSetListener | null, ct: CancelToken | null) {
    this.errors = new Set<string>(progress.errors ?? []);
    ct?.promise.then(() => this.fireCancelling()).catch(noop);
  }

  setTransientError(error: any, attempts: number, retryPause?: number): unknown {
    const msg = exceptionSummary(error);
    this.addErrors([msg]);
    this.fire();
    return msg;
  }

  clearTransientError(cookie: unknown): void {
    const msg = cookie as string;
    this.removeError(msg);
    this.fire();
  }

  addErrors(moreErrors: string[]) {
    if (moreErrors.length == 0)
      return;

    for (const err of moreErrors) {
      this.errors.add(err);
    }

    this.progress.errors = Array.from(this.errors.values());
  }

  removeError(err: string) {
    this.errors.delete(err);
    this.progress.errors = Array.from(this.errors.values());
  }

  fireFailure(exception: any) {
    this.addErrors([exceptionAnalysis(exception)]);
    this.progress.conclusion = OperationConclusion.FAILED;
    this.fireActivity('Failed', exceptionSummary(exception));
  }

  fireCancel(cancelledActivity: string) {
    this.progress.conclusion = OperationConclusion.CANCELLED;
    this.fireActivity('Cancelled', cancelledActivity + ' was cancelled before completion');
  }

  fireSucceed(status: string, description: string) {
    this.progress.conclusion = OperationConclusion.SUCCEEDED;
    this.fireActivity(status, description);
  }

  fireActivity(status: string, description: string) {
    this.progress.status = status;
    this.progress.description = description;
    this.fire();
  }

  fireCancelling() {
    this.cancelling = true;
    this.fire();
  }

  fire() {
    if (this.progress.conclusion == OperationConclusion.PENDING) {
      if (this.cancelling) {
        this.progress.status = 'Cancelling';
        this.progress.percentage = 0;
      }
    } else {
      // Only pending progress has percentage
      this.progress.percentage = undefined;
    }

    if (this.listener) {
      try {
        this.listener(this.progress);
      } catch (ex) {
        this.addErrors([exceptionAnalysis(ex)]);
      }
    }
  }

  /*
    Run an asynchronous task, and conclude progress based on how the task ended.

    If promise was resolved, then progress concludes with success.

    If promise was rejected with an Axios.Cancel, then progress concludes with
    cancellation.

    If promise was rejected with a different cause, then the progress concludes
    with failure.

    Any errors thrown by the task will be propagated upwards.
  */
  async concludeTask<T>(successStatus: string,  activity: string, task: () => Promise<T>): Promise<T> {
    const result = await this.concludePartialTaskCore(successStatus, activity, task);
    this.fireSucceed(successStatus, activity + ' completed');
    return result;
  }

  async concludePartialTask<T>(successStatus: string,  activity: string, task: () => Promise<T>): Promise<T> {
    const result = await this.concludePartialTaskCore(successStatus, activity, task);
    this.fireActivity(successStatus, activity + ' completed');
    return result;
  }

  private async concludePartialTaskCore<T>(successStatus: string,  activity: string, task: () => Promise<T>): Promise<T> {
    try {
      return await task();
    } catch (ex) {
      if (Axios.isCancel(ex)) {
        this.fireCancel(activity);

      } else {
        this.fireFailure(ex);
      }

      throw ex;
    }
  }

  /*
    Make listener that aggregate progress deltas for a sub-operation, so that we
    do not need to track and repeatedly sum all of the individual file progress.
  */
  makeDeltaListener() {
    let lastFilesDone = 0;
    let lastBytesDone = 0;
    const deltaListener: FileSetListener = (progress: FileSetProgress) => {
      this.progress.files.done += progress.files.done - lastFilesDone;
      lastFilesDone = progress.files.done;
      this.progress.bytes.done += progress.bytes.done - lastBytesDone;
      lastBytesDone = progress.bytes.done;

      this.progress.status = progress.status;
      this.progress.description = progress.description ?? this.progress.description;

      // report uploadMethod from individual uploads and combine them on the top level progress info
      this.progress.uploadMethods = {...this.progress.uploadMethods, ...progress.uploadMethods };
      this.addErrors(progress.errors);

      this.refreshPercentage();
      this.fire();
    };

    return deltaListener;
  }

  public refreshPercentage() {
    const bytes = this.progress.bytes;
    this.progress.percentage = 100 * bytes.done / bytes.known;
  }
}

/**
 * Conclude a task by combining the retry mechanism described by the resilience
 * parameter with the progress reporing mechanism described by the
 * progressBuilder parameter.
 *
 * Expects task to be idempotent, and that progressBuilder state is not modified
 * by concurrent activities outside of the task.
 */
export async function resilientlyConcludeTask<T>(
  progressBuilder: ProgressBuilder,
  tc: TaskContext,
  successStatus: string,
  activity: string,
  task: () => Promise<T>,
  structuredLogger: StructuredLogger,
): Promise<T> {
  const onRollback = makeRollbackCheckpoint(progressBuilder, activity, structuredLogger);
  const result = await progressBuilder.concludeTask(successStatus, activity, () => {
    return runTask({ parent: tc, onRollback }, task);
  });

  assert(progressBuilder.progress.files.done == progressBuilder.progress.files.known, 'Expected ' + progressBuilder.progress.files.known + ' file(s), got ' + progressBuilder.progress.files.done);
  assert(progressBuilder.progress.bytes.done == progressBuilder.progress.bytes.known, 'Expected ' + progressBuilder.progress.bytes.known + ' byte(s), got ' + progressBuilder.progress.bytes.done);
  return result;
}

export function resilientlyConcludePartialTask<T>(
  progressBuilder: ProgressBuilder,
  tc: TaskContext,
  successStatus: string,
  activity: string,
  task: () => Promise<T>,
  structuredLogger: StructuredLogger,
): Promise<T> {
  const onRollback = makeRollbackCheckpoint(progressBuilder, activity, structuredLogger);
  return progressBuilder.concludePartialTask(successStatus, activity, () => {
    return runTask({ parent: tc, onRollback }, task);
  });
}

/**
 * Store the current progress, so that we can roll back to it, in case a subtask
 * fails.
 */
function makeRollbackCheckpoint(progressBuilder: ProgressBuilder, activity: string, structuredLogger: StructuredLogger): RollbackHandler {
  const initialFiles = progressBuilder.progress.files.done;
  const initialBytes = progressBuilder.progress.bytes.done;
  return (ms: number, cause: any) => {
    // Roll back progress and report retry intent
    progressBuilder.progress.files.done = initialFiles;
    progressBuilder.progress.bytes.done = initialBytes;
    progressBuilder.fireActivity(
      'Will retry',
      activity + ' will be tried again later: ' + exceptionSummary(cause)
    );
    structuredLogger.log({
      ...allExceptionDetails(cause),
      scope: 'resilientlyConcludeTask',
      activity,
      level: 'warn',
      willRetry: true
    });
  };
}
